/* eslint-disable max-lines-per-function */
import React from "react";
import MaterialTable, { Column, MTableToolbar } from "@material-table/core";
import { Link, useNavigate, useParams } from "react-router-dom";
import { gql } from "graphql-tag";
import moment, { Moment } from "moment";
import { useMutation, useQuery } from "@apollo/client";
import { DatePicker } from "@mui/x-date-pickers";
import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import { invoicedTrackingQueries, invoicedTrackingQueries_invoicedTrackings as invoicedTrackings, RelatedEntityType, sendTrackingToInvoiced } from "types/gql-types";
import { Button, LinkButton } from "components/haven-design";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";

const BILLING_BY_MONTH = gql`
  query invoicedTrackingQueries($month: Date!) {
    invoicedTrackings(month: $month) {
      id
      month
      regularQuantity
      overageQuantity
      sentToInvoiced
      invoicedCustomer {
        relatedEntityType
        property {
          id
          name
          organization {
            id
            name
          }
        }
        organization {
          id
          name
        }
      }
    }
  }
`;

const SEND_TO_INVOICED = gql`
  mutation sendTrackingToInvoiced($trackingIds: [ID!]!) {
    sendTrackingToInvoiced(trackingIds: $trackingIds)
  }
`;

const getOrg = ({ invoicedCustomer }:invoicedTrackings) =>
  invoicedCustomer.organization ?? invoicedCustomer.property?.organization;

const renderOrgColumn =
  (invoicedTracking:invoicedTrackings) => {
    const { month } = invoicedTracking;
    const org = getOrg(invoicedTracking);

    if (!org) {
      return null;
    }

    return <Link to={`/dash/admin/billing/letters/o/${org.id}/${moment(month).format("MM-YYYY")}`}>{org.name}</Link>;
  };

const renderPropColumn =
    ({ month, invoicedCustomer: { property, relatedEntityType } }:invoicedTrackings) => {
      if (relatedEntityType === RelatedEntityType.ORGANIZATION) {
        return <em>all properties</em>;
      }

      return <Link to={`/dash/admin/billing/letters/p/${property?.id}/${moment(month).format("MM-YYYY")}`}>{property?.name}</Link>;
    };

const columns: Array<Column<invoicedTrackings>> = [
  { title: "Month", field: "month", render: ({ month }) => moment(month).format("MM/YYYY") },
  { title: "Organization",
    field: "invoicedCustomer.property.organization.name",
    render: renderOrgColumn },
  { title: "Property", field: "invoicedCustomer.property.name", render: renderPropColumn },
  { title: "Sent to Invoiced", field: "sentToInvoiced", type: "boolean" },
  { title: "Letters", field: "regularQuantity" },
  { title: "Additional Pages", field: "overageQuantity" },
];

const filterAndSortTableData = (data:invoicedTrackingQueries | undefined, showZeroes:boolean) => {
  if (!data || !data.invoicedTrackings) return [];

  const filterZeroes = (tracking: invoicedTrackings) =>
    tracking.overageQuantity !== 0 || tracking.regularQuantity !== 0;

  const resultSet =
    showZeroes ? data.invoicedTrackings : data.invoicedTrackings.filter(filterZeroes);

  return resultSet
    .sort((a, b) => {
      if (a.sentToInvoiced !== b.sentToInvoiced) return a.sentToInvoiced ? -1 : 1;

      if (getOrg(a)?.name !== getOrg(b)?.name) return getOrg(a)?.name.localeCompare(getOrg(b)?.name ?? "") ?? 0;

      return a.invoicedCustomer.property?.name.localeCompare(b.invoicedCustomer.property?.name ?? "") ?? 0;
    });
};

const navigateToMonth = (
  navigateFn: ReturnType<typeof useNavigate>,
  month?: Date | Moment | null,
) => {
  const monthString = moment(month).startOf("month").format("MM-YYYY");
  navigateFn(`/dash/admin/billing/${monthString}`);
};

const BillingSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const { month: monthParam } = useParams() as { month?: string };

  React.useEffect(() => {
    if (!monthParam) {
      navigateToMonth(navigate);
    }
  }, [monthParam, navigate]);

  const [month, setMonth] = React.useState(
    monthParam ? moment(monthParam, "MM-YYYY").startOf("month") : moment().startOf("month"),
  );
  const [showZeroes, setShowZeroes] = React.useState(false);
  const { data, error, loading, refetch } =
    useQuery<invoicedTrackingQueries>(BILLING_BY_MONTH, {
      variables: { month: month.format("YYYY-MM-DD") },
      fetchPolicy: "no-cache",
    });
  const [sendToInvoiced] =
    useMutation<sendTrackingToInvoiced>(SEND_TO_INVOICED);
  const { openSnackbar } = useSnackbar();

  const sendUnsent = () => {
    if (!data || !data.invoicedTrackings || data.invoicedTrackings.length === 0) return;

    const trackingIds = data.invoicedTrackings
      .filter(({ sentToInvoiced }) => !sentToInvoiced)
      .map(({ id }) => id);

    sendToInvoiced({ variables: { trackingIds } })
      .then(() => {
        openSnackbar({ message: "Successfully sent items to Invoiced.", status: "success" });
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: "Error sending items to Invoiced.", status: "error" });
      })
      .finally(() => {
        refetch();
      });
  };

  if (error) return <span>Server Error</span>;

  const tableData: invoicedTrackings[] = filterAndSortTableData(data, showZeroes);

  const hasUnsent = tableData.some(({ sentToInvoiced }) => !sentToInvoiced);

  return <MaterialTable
    title="Billing"
    data={tableData}
    columns={columns}
    isLoading={loading}
    options={{ pageSize: 50 }}

    components={{ Toolbar: function Toolbar (props) {
      return (
        <><div>
          <MTableToolbar {...props} />
        </div>
        <Grid container style={{ paddingLeft: 10 }}>
          <Grid size={{ xs: 6 }}>
            <DatePicker
              slotProps={{
                textField: {
                  label: "Month",
                },
              }}
              value={month.toDate()}
              format="MM/yyyy"
              openTo="month"
              views={["year", "month"]}
              closeOnSelect
              onChange={(date: Date | null) => {
                if (date instanceof Date && !isNaN(date.getTime())) {
                  setMonth(moment(date).startOf("month"));
                  navigateToMonth(navigate, date);
                }
              }} />
            <FormControlLabel
              style={{ paddingTop: 8, paddingLeft: 16 }}
              control={<Checkbox checked={showZeroes}
                onClick={() => setShowZeroes(!showZeroes)} />}
              label="Include Zeroes" />
          </Grid>
          <Grid container size={{ xs: 6 }} justifyContent="flex-end" spacing={2}>
            <Grid>
              <LinkButton color="secondary" to={`/dash/admin/billing/new/${moment(month).startOf("month").format("MM-YYYY")}`}>
                New Monthly Billings
              </LinkButton>
            </Grid>
            <Grid>
              <Button
                disabled={!hasUnsent}
                onClick={() => sendUnsent()} color="primary">
                Send Unsent to Invoiced
              </Button>
            </Grid>
          </Grid>
        </Grid></>
      );
    } }}
  />;
};

export default BillingSearchPage;
